import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import cccCalendarApi, { CccCalendarEvent } from '../api/cccCalendarApi';
import cccApi from '../api/cccApi';
import CccCalendarEventModal from '../components/CccCalendarEventModal';
import CccCalendarSidebar, {
  ALL_CLIENTS_ON,
  type ClientFilterState,
} from '../components/calendar/CccCalendarSidebar';
import CccEventPopover from '../components/calendar/CccEventPopover';
import CccMonthGrid from '../components/calendar/CccMonthGrid';
import CccWeekGrid from '../components/calendar/CccWeekGrid';
import {
  CalendarView,
  CccCalEvent,
  addDays,
  addMonths,
  countConflictPairs,
  endOfMonth,
  findConflictingEventIds,
  formatDateLong,
  formatMonthLabel,
  formatWeekRangeShort,
  parseIsoLocal,
  startOfMonth,
  startOfWeek,
  toCccCalEvent,
  toDateKey,
} from '../components/calendar/cccCalTypes';
import type { CccTeamUserOption } from '../components/CccTeamUserDrawer';
import { buildClientNavigateUrl, type CccClientType } from '../config/cccClientRoutes';
import { useCccAuth } from '../contexts/CccAuthContext';
import { isCccCoordenadorOrAdmin } from '../utils/cccUserManagement';
import '../styles/cccGrhCalendar.css';
import '../styles/cccCalendarGrhAdapt.css';
import '../styles/cccCalendar.css';

const VIEWS: Array<{ id: CalendarView; label: string }> = [
  { id: 'day', label: 'Dia' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mês' },
];

const rangeIsoForRequest = (start: Date, end: Date) => ({
  from: `${toDateKey(start)}T00:00:00`,
  to: `${toDateKey(end)}T00:00:00`,
});

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useCccAuth();

  const [today] = useState(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  });
  const [view, setView] = useState<CalendarView>('week');
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [miniMonthDate, setMiniMonthDate] = useState<Date>(startOfMonth(today));

  const [rawEvents, setRawEvents] = useState<CccCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [clientFilter, setClientFilter] = useState<ClientFilterState>(ALL_CLIENTS_ON);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CccCalendarEvent | null>(null);
  const [draftStart, setDraftStart] = useState<Date>(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    return d;
  });
  const [draftEnd, setDraftEnd] = useState<Date>(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 2);
    return d;
  });

  const [popoverEvent, setPopoverEvent] = useState<CccCalEvent | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<DOMRect | null>(null);
  const [popoverBusy, setPopoverBusy] = useState(false);

  const [teamUsers, setTeamUsers] = useState<CccTeamUserOption[]>([]);
  const [calendarUserId, setCalendarUserId] = useState<number | 'all' | null>(null);
  const [userSearch, setUserSearch] = useState('');

  const canManageCalendarScope = useMemo(() => isCccCoordenadorOrAdmin(user), [user]);

  const currentUserId = useMemo(() => {
    const raw = user?.id;
    if (raw === null || raw === undefined || raw === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [user]);

  useEffect(() => {
    if (!canManageCalendarScope) {
      setCalendarUserId(currentUserId);
      return;
    }
    if (calendarUserId === null) setCalendarUserId('all');
  }, [canManageCalendarScope, currentUserId, calendarUserId]);

  useEffect(() => {
    if (!canManageCalendarScope) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await cccApi.getCccDashboardUsers();
        if (cancelled || !res.success) return;
        const options = (res.users || [])
          .map((u) => ({
            id: Number(u.id),
            name: String(u.name || u.email || `Utilizador ${u.id}`),
            email: u.email ? String(u.email) : undefined,
          }))
          .filter((u) => Number.isFinite(u.id))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt'));
        setTeamUsers(options);
      } catch {
        if (!cancelled) setTeamUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageCalendarScope]);

  const listScopeUserId = useMemo((): number | 'all' | undefined => {
    if (!canManageCalendarScope) return undefined;
    if (calendarUserId === 'all' || calendarUserId === null) return 'all';
    return calendarUserId;
  }, [canManageCalendarScope, calendarUserId]);

  const userNameById = useMemo(() => {
    const map: Record<number, string> = {};
    if (currentUserId != null) {
      const selfName = String(user?.name || user?.username || '').trim();
      if (selfName) map[currentUserId] = selfName;
    }
    teamUsers.forEach((u) => {
      map[u.id] = u.name;
    });
    return map;
  }, [currentUserId, user, teamUsers]);

  const calendarSelectionLabel = useMemo(() => {
    if (!canManageCalendarScope) return '';
    if (calendarUserId === 'all' || calendarUserId === null) return 'Toda a equipa';
    return userNameById[calendarUserId] || 'Utilizador selecionado';
  }, [canManageCalendarScope, calendarUserId, userNameById]);

  const visibleRange = useMemo(() => {
    if (view === 'day') {
      const start = new Date(selectedDate);
      start.setHours(0, 0, 0, 0);
      const end = addDays(start, 1);
      return { start, end, days: [start] };
    }
    if (view === 'week') {
      const start = startOfWeek(selectedDate);
      const end = addDays(start, 7);
      return { start, end, days: Array.from({ length: 7 }, (_, i) => addDays(start, i)) };
    }
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    const gridStart = startOfWeek(monthStart);
    const gridEnd = addDays(gridStart, 42);
    return { start: gridStart, end: gridEnd, days: [monthStart, monthEnd] };
  }, [view, selectedDate]);

  const periodLabel = useMemo(() => {
    if (view === 'day') return formatDateLong(selectedDate);
    if (view === 'week') return formatWeekRangeShort(startOfWeek(selectedDate));
    return formatMonthLabel(selectedDate);
  }, [view, selectedDate]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { start, end } = visibleRange;
      const { from, to } = rangeIsoForRequest(start, end);
      const res = await cccCalendarApi.listEvents(from, to, listScopeUserId);
      setRawEvents(res.events || []);
    } catch (e) {
      console.error('[CalendarPage] load failed', e);
      setError('Não foi possível carregar o calendário');
      setRawEvents([]);
    } finally {
      setLoading(false);
    }
  }, [visibleRange, listScopeUserId]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    setMiniMonthDate(startOfMonth(selectedDate));
  }, [selectedDate]);

  const events = useMemo(
    () =>
      rawEvents.map((ev) =>
        toCccCalEvent(ev, userNameById[ev.user_id] || null),
      ),
    [rawEvents, userNameById],
  );

  const filteredEvents = useMemo(
    () => events.filter((ev) => clientFilter[ev.tone]),
    [events, clientFilter],
  );

  const conflictIds = useMemo(
    () => findConflictingEventIds(events),
    [events],
  );

  const conflictPairCount = useMemo(
    () => countConflictPairs(filteredEvents),
    [filteredEvents],
  );

  const eventDays = useMemo(() => {
    const set = new Set<string>();
    filteredEvents.forEach((ev) => {
      set.add(toDateKey(parseIsoLocal(ev.starts_at)));
    });
    return set;
  }, [filteredEvents]);

  const jumpToFirstConflict = () => {
    const conflicting = filteredEvents
      .filter((ev) => conflictIds.has(ev.id))
      .sort(
        (a, b) =>
          parseIsoLocal(a.starts_at).getTime() - parseIsoLocal(b.starts_at).getTime(),
      );
    if (!conflicting.length) return;
    setSelectedDate(parseIsoLocal(conflicting[0].starts_at));
    if (view === 'month') setView('day');
  };

  const handlePrev = () => {
    if (view === 'day') setSelectedDate((d) => addDays(d, -1));
    else if (view === 'week') setSelectedDate((d) => addDays(d, -7));
    else setSelectedDate((d) => addMonths(d, -1));
  };

  const handleNext = () => {
    if (view === 'day') setSelectedDate((d) => addDays(d, 1));
    else if (view === 'week') setSelectedDate((d) => addDays(d, 7));
    else setSelectedDate((d) => addMonths(d, 1));
  };

  const handleToday = () => {
    setSelectedDate(today);
    setMiniMonthDate(startOfMonth(today));
  };

  const openCreate = (start?: Date | null, end?: Date | null) => {
    const s =
      start ||
      (() => {
        const d = new Date(selectedDate);
        d.setHours(9, 0, 0, 0);
        return d;
      })();
    const e =
      end ||
      (() => {
        const d = new Date(s);
        d.setHours(s.getHours() + 1);
        return d;
      })();
    setEditing(null);
    setDraftStart(s);
    setDraftEnd(e);
    setModalOpen(true);
  };

  const openEdit = (ev: CccCalEvent) => {
    setEditing(ev.source);
    setDraftStart(parseIsoLocal(ev.starts_at));
    setDraftEnd(parseIsoLocal(ev.ends_at));
    setModalOpen(true);
  };

  const closePopover = () => {
    setPopoverEvent(null);
    setPopoverAnchor(null);
    setPopoverBusy(false);
  };

  const handleDelete = async () => {
    if (!popoverEvent) return;
    if (!window.confirm('Eliminar este evento?')) return;
    setPopoverBusy(true);
    try {
      const res = await cccCalendarApi.deleteEvent(popoverEvent.id);
      if (!res.success) {
        message.error(res.message || 'Erro ao eliminar');
        return;
      }
      closePopover();
      void loadEvents();
    } catch {
      message.error('Erro ao eliminar evento');
    } finally {
      setPopoverBusy(false);
    }
  };

  const openView = view === 'month' ? 'month' : view;

  return (
    <div className="grh-cal-page ccc-grh-cal-page">
      <div className="grh-cal-topbar">
        <div className="grh-cal-topbar-left">
          <button type="button" className="grh-cal-today-btn" onClick={handleToday}>
            Hoje
          </button>
          <div className="grh-cal-nav">
            <button type="button" onClick={handlePrev} aria-label="Anterior">
              ‹
            </button>
            <button type="button" onClick={handleNext} aria-label="Seguinte">
              ›
            </button>
          </div>
          <h1 className="grh-cal-title">{periodLabel}</h1>
          {canManageCalendarScope && calendarSelectionLabel && (
            <span className="ccc-cal-scope-pill">{calendarSelectionLabel}</span>
          )}
        </div>
        <div className="grh-cal-topbar-right">
          {conflictPairCount > 0 && (
            <button
              type="button"
              className="grh-cal-conflict-btn"
              onClick={jumpToFirstConflict}
              title={`${conflictPairCount} conflito(s) de horário no período`}
            >
              ⚠ {conflictPairCount} conflito{conflictPairCount === 1 ? '' : 's'}
            </button>
          )}
          <div className="grh-cal-view-switch" role="group" aria-label="Vista">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                className={openView === v.id ? 'is-active' : ''}
                onClick={() => setView(v.id)}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="grh-cal-banner" role="status">
          {error}
          <button type="button" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      <div className="grh-cal-body">
        <CccCalendarSidebar
          monthDate={miniMonthDate}
          selectedDate={selectedDate}
          today={today}
          eventDays={eventDays}
          onChangeMonth={setMiniMonthDate}
          onSelectDate={(d) => {
            setSelectedDate(d);
            if (view === 'month') setView('day');
          }}
          onCreate={() => openCreate()}
          clientFilter={clientFilter}
          onClientFilterChange={setClientFilter}
          canPickUser={canManageCalendarScope}
          teamUsers={teamUsers}
          calendarUserId={calendarUserId}
          onPickUser={setCalendarUserId}
          userSearch={userSearch}
          onUserSearchChange={setUserSearch}
        />

        <div className={`grh-cal-main ${loading ? 'is-loading' : ''}`}>
          {view === 'month' ? (
            <CccMonthGrid
              monthDate={selectedDate}
              today={today}
              events={filteredEvents}
              conflictIds={conflictIds}
              onSelectEvent={(ev, anchor) => {
                setPopoverEvent(ev);
                setPopoverAnchor(anchor);
              }}
              onOpenDay={(d) => {
                setSelectedDate(d);
                setView('day');
              }}
            />
          ) : (
            <CccWeekGrid
              days={visibleRange.days}
              events={filteredEvents}
              today={today}
              conflictIds={conflictIds}
              onSelectEvent={(ev, anchor) => {
                setPopoverEvent(ev);
                setPopoverAnchor(anchor);
              }}
              onSelectRange={(start, end) => openCreate(start, end)}
            />
          )}
        </div>
      </div>

      {popoverEvent && popoverAnchor && (
        <CccEventPopover
          event={popoverEvent}
          anchor={popoverAnchor}
          onClose={closePopover}
          onEdit={() => {
            const ev = popoverEvent;
            closePopover();
            openEdit(ev);
          }}
          onDelete={() => void handleDelete()}
          onOpenClient={
            popoverEvent.source.client_type !== 'none' && popoverEvent.source.client_ref
              ? () => {
                  const src = popoverEvent.source;
                  closePopover();
                  navigate(
                    buildClientNavigateUrl(
                      src.client_type as CccClientType,
                      src.client_ref,
                    ),
                  );
                }
              : undefined
          }
          busy={popoverBusy}
        />
      )}

      <CccCalendarEventModal
        open={modalOpen}
        initialStart={draftStart}
        initialEnd={draftEnd}
        event={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={() => {
          void loadEvents();
          setModalOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
};

export default CalendarPage;
